/**
 * Front page hero: ScrollTrigger pin + scrub — idle scroll, reverse typewriter, model exits down;
 * scroll up reverses. Adds hero-section--past / --canvas-dismissed when leaving pin.
 * Phase lengths (vh) control how much wheel scroll maps to each phase — larger = slower.
 * Lenis + high scrub lag + lower wheelMultiplier while pin active: one wheel flick moves less through the scene.
 */
(function () {
	'use strict';

	/** Seconds of scrub smoothing — higher = animation trails scroll more */
	var SCRUB_LAG_SEC = 4.8;

	/** Lenis wheel/touch multipliers while hero pin is active (saved & restored) */
	var LENIS_WHEEL_FACTOR = 0.22;
	var LENIS_TOUCH_FACTOR = 0.42;

	var PHASE_SLOWDOWN = 100;
	var IDLE_VH = 1.5 * PHASE_SLOWDOWN;
	var TYPE_VH = 1.25 * PHASE_SLOWDOWN;
	var MODEL_VH = 1.1 * PHASE_SLOWDOWN;

	var initAttempts = 0;
	var maxInitAttempts = 50;

	function getScrollTriggerPlugin() {
		if (typeof gsap === 'undefined') return null;
		if (gsap.ScrollTrigger) return gsap.ScrollTrigger;
		if (typeof window !== 'undefined' && window.ScrollTrigger) {
			if (gsap.registerPlugin) gsap.registerPlugin(window.ScrollTrigger);
			return window.ScrollTrigger;
		}
		if (typeof ScrollTrigger !== 'undefined') {
			if (gsap.registerPlugin) gsap.registerPlugin(ScrollTrigger);
			return ScrollTrigger;
		}
		return null;
	}

	function splitTextIntoChars(container) {
		if (!container || !container.childNodes.length) return [];
		var chars = [];
		function createCharSpan(ch) {
			var span = document.createElement('span');
			span.className = 'hero-section__char';
			span.style.display = 'inline-block';
			span.textContent = ch === ' ' ? '\u00A0' : ch;
			chars.push(span);
			return span;
		}
		function processTextNode(text, parent, beforeNode) {
			if (!text || text.length === 0) return;
			var words = text.split(/(\s+)/);
			for (var i = 0; i < words.length; i++) {
				var token = words[i];
				var toInsert;
				if (/^\s+$/.test(token)) {
					for (var j = 0; j < token.length; j++) {
						var ch = token[j];
						if (ch !== '\n' && ch !== '\r' && ch !== '\t') {
							toInsert = createCharSpan(ch);
							parent.insertBefore(toInsert, beforeNode);
						}
					}
				} else if (token.length > 0) {
					var wordWrap = document.createElement('span');
					wordWrap.className = 'hero-section__phrase-word';
					for (var k = 0; k < token.length; k++) {
						wordWrap.appendChild(createCharSpan(token[k]));
					}
					parent.insertBefore(wordWrap, beforeNode);
				}
			}
		}
		function walk(node) {
			if (node.nodeType === Node.TEXT_NODE) {
				var text = node.textContent || '';
				var parent = node.parentNode;
				processTextNode(text, parent, node);
				parent.removeChild(node);
			} else if (node.nodeType === Node.ELEMENT_NODE && (!node.classList || !node.classList.contains('hero-section__char'))) {
				Array.prototype.forEach.call(node.childNodes, walk);
			}
		}
		Array.prototype.forEach.call(container.childNodes, walk);
		return chars;
	}

	/** Backspace-from-end: eraseProgress 0 = all visible, 1 = all hidden */
	function applyEraseProgress(chars, eraseProgress) {
		var n = chars.length;
		if (n === 0) return;
		var p = Math.max(0, Math.min(1, eraseProgress));
		for (var i = 0; i < n; i++) {
			var visible = p < (n - i) / n;
			gsap.set(chars[i], { opacity: visible ? 1 : 0, y: visible ? 0 : 4 });
		}
	}

	function initHeroScroll() {
		if (!document.querySelector('.front-page')) return;

		var heroSection = document.getElementById('hero-section');
		if (!heroSection || !heroSection.classList.contains('hero-section--with-features')) return;

		if (typeof window.matchMedia !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			return;
		}

		if (typeof gsap === 'undefined') {
			initAttempts++;
			if (initAttempts < maxInitAttempts) setTimeout(initHeroScroll, 100);
			return;
		}

		var ScrollTriggerPlugin = getScrollTriggerPlugin();
		if (!ScrollTriggerPlugin || (typeof ScrollTriggerPlugin.create !== 'function' && typeof ScrollTriggerPlugin !== 'function')) {
			initAttempts++;
			if (initAttempts < maxInitAttempts) setTimeout(initHeroScroll, 100);
			return;
		}

		var wordEl = heroSection.querySelector('[data-hero-word]');
		var modelImg = heroSection.querySelector('.hero-section__model-placeholder');
		var chars = [];

		if (wordEl) {
			var wrap = heroSection.querySelector('[data-hero-word-block]');
			var fromAttr = wrap && wrap.getAttribute('data-hero-word-value');
			if (fromAttr) {
				wordEl.textContent = fromAttr;
			}
			chars = splitTextIntoChars(wordEl);
			applyEraseProgress(chars, 0);
		}

		var totalVh = IDLE_VH + TYPE_VH + MODEL_VH;
		var idleEnd = IDLE_VH / totalVh;
		var typeEnd = (IDLE_VH + TYPE_VH) / totalVh;

		var easeType = typeof gsap.parseEase === 'function' ? gsap.parseEase('power1.inOut') : function (u) {
			return u;
		};
		var easeModel = typeof gsap.parseEase === 'function' ? gsap.parseEase('power3.inOut') : function (u) {
			return u;
		};

		var lenisSavedWheel = null;
		var lenisSavedTouch = null;

		function applyLenisHeroSlowdown(active) {
			var L = window.evonaLenis;
			if (!L || !L.options) return;
			if (active) {
				if (lenisSavedWheel === null) {
					lenisSavedWheel = typeof L.options.wheelMultiplier === 'number' ? L.options.wheelMultiplier : 1;
					lenisSavedTouch = typeof L.options.touchMultiplier === 'number' ? L.options.touchMultiplier : 1;
				}
				L.options.wheelMultiplier = lenisSavedWheel * LENIS_WHEEL_FACTOR;
				L.options.touchMultiplier = lenisSavedTouch * LENIS_TOUCH_FACTOR;
			} else if (lenisSavedWheel !== null) {
				L.options.wheelMultiplier = lenisSavedWheel;
				L.options.touchMultiplier = lenisSavedTouch;
				lenisSavedWheel = null;
				lenisSavedTouch = null;
			}
		}

		function syncLenisSlowdownFromSt(self) {
			var pinActive = !!self.isActive;
			applyLenisHeroSlowdown(pinActive);
		}

		function applyFromProgress(p) {
			p = Math.max(0, Math.min(1, p));
			var eraseProgress = 0;
			var modelProgress = 0;

			if (p <= idleEnd) {
				eraseProgress = 0;
				modelProgress = 0;
			} else if (p <= typeEnd) {
				var t = (p - idleEnd) / (typeEnd - idleEnd);
				eraseProgress = easeType(t);
				modelProgress = 0;
			} else {
				eraseProgress = 1;
				modelProgress = (p - typeEnd) / (1 - typeEnd);
			}

			if (chars.length) {
				applyEraseProgress(chars, eraseProgress);
			}

			if (modelImg) {
				var vh = window.innerHeight || 800;
				var yEased = easeModel(modelProgress);
				var yDown = yEased * vh * 1.05;
				gsap.set(modelImg, { xPercent: -46, x: 0, y: yDown, force3D: true });
			}
		}

		function setPastState(isPast) {
			if (isPast) {
				heroSection.classList.add('hero-section--past');
				if (heroSection.getAttribute('data-hero-image-only') === 'true') {
					heroSection.classList.add('hero-section--canvas-dismissed');
				}
			} else {
				heroSection.classList.remove('hero-section--past');
				heroSection.classList.remove('hero-section--canvas-dismissed');
			}
		}

		var st = (ScrollTriggerPlugin.create || ScrollTriggerPlugin)({
			trigger: heroSection,
			start: 'top top',
			end: '+=' + totalVh + 'vh',
			pin: true,
			pinSpacing: true,
			scrub: SCRUB_LAG_SEC,
			anticipatePin: 1,
			invalidateOnRefresh: true,
			onUpdate: function (self) {
				syncLenisSlowdownFromSt(self);
				applyFromProgress(self.progress);
			},
			onLeave: function () {
				applyLenisHeroSlowdown(false);
				setPastState(true);
			},
			onEnterBack: function () {
				setPastState(false);
			},
			onLeaveBack: function () {
				applyLenisHeroSlowdown(false);
			}
		});

		syncLenisSlowdownFromSt(st);
		applyFromProgress(st.progress);
		if (st.progress >= 0.999) {
			applyLenisHeroSlowdown(false);
			setPastState(true);
		} else {
			setPastState(false);
		}

		window.addEventListener(
			'resize',
			function () {
				syncLenisSlowdownFromSt(st);
				applyFromProgress(st.progress);
			},
			{ passive: true }
		);
	}

	/** Features exit phase calls this when blocks exist; hero scroll owns the model here — no-op. */
	window.evonaHeroSetExitProgress = function () {};

	window.evonaHeroAfterLogoHiddenOnScrollUp = function () {};
	window.evonaHeroCancelRobotReveal = function () {};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initHeroScroll);
	} else {
		initHeroScroll();
	}
})();
